'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  // Create Account state
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const supabase = createClient();
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
        return;
      }

      // Check if account is active (not pending approval)
      if (signInData?.user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('is_active')
          .eq('id', signInData.user.id)
          .single();

        if (profile && profile.is_active === false) {
          // Sign them back out — account not yet enabled
          await supabase.auth.signOut();
          setError('Your account is pending approval. You will receive an email once your account has been activated.');
          setLoading(false);
          return;
        }
      }

      // Redirect to MFA verification step
      router.refresh();
      router.push('/mfa-verify');
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRegError('');
    setRegSuccess('');

    if (!regEmail.trim()) {
      setRegError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) {
      setRegError('Please enter a valid email address.');
      return;
    }
    if (!regPassword) {
      setRegError('Please enter a password.');
      return;
    }
    if (regPassword.length < 6) {
      setRegError('Password must be at least 6 characters.');
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setRegError('Passwords do not match.');
      return;
    }

    setRegLoading(true);

    try {
      // Call the register-portal-user edge function
      const supabase = createClient();
      const { data, error: fnError } = await supabase.functions.invoke('register-portal-user', {
        body: { action: 'self_register', email: regEmail.trim(), password: regPassword },
      });

      if (fnError || data?.error) {
        let msg = 'Registration failed. Please try again.';
        if (fnError) {
          try {
            const errBody = await (fnError as any).context?.json?.();
            msg = errBody?.error ?? fnError.message ?? msg;
          } catch {
            msg = fnError.message ?? msg;
          }
        } else if (data?.error) {
          msg = data.error;
        }
        setRegError(msg);
        setRegLoading(false);
        return;
      }

      // Send notification email to support
      await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: 'support@mdgp.co.uk',
          subject: 'New Portal Account Request',
          body: `A new account registration request has been received.\n\nEmail: ${regEmail.trim()}\n\nThe account has been created and is currently DISABLED, assigned to "AAA Pending Users".\n\nPlease log in to Admin Settings > Portal Users to review this request, assign the user to the correct company, and enable their account.`,
          htmlBody: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
            <div style="background:#1B365D;padding:20px 24px;border-radius:8px 8px 0 0">
              <h2 style="color:#ffffff;margin:0;font-size:18px">New Portal Account Request</h2>
            </div>
            <div style="background:#f9fafb;padding:24px;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px">
              <p style="color:#374151;font-size:14px;margin:0 0 16px">A new account registration request has been received:</p>
              <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
                <tr>
                  <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:13px;color:#6b7280;width:120px">Email</td>
                  <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:13px;color:#111827;font-weight:600">${regEmail.trim()}</td>
                </tr>
                <tr>
                  <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:13px;color:#6b7280">Status</td>
                  <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:13px;color:#dc2626;font-weight:600">DISABLED — Pending Approval</td>
                </tr>
                <tr>
                  <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:13px;color:#6b7280">Company</td>
                  <td style="padding:8px 12px;background:#fff;border:1px solid #e5e7eb;font-size:13px;color:#111827">AAA Pending Users</td>
                </tr>
              </table>
              <p style="color:#374151;font-size:14px;margin:0 0 8px">To approve this account:</p>
              <ol style="color:#374151;font-size:14px;margin:0;padding-left:20px">
                <li style="margin-bottom:4px">Log in to the Helpdesk Portal</li>
                <li style="margin-bottom:4px">Go to <strong>Admin Settings → Portal Users</strong></li>
                <li style="margin-bottom:4px">Find the user and click <strong>Edit</strong></li>
                <li style="margin-bottom:4px">Assign them to the correct company and enable the account</li>
              </ol>
            </div>
          </div>`,
        }),
      });

      setRegSuccess('Account request submitted! You will receive an email once your account has been approved.');
      setRegEmail('');
      setRegPassword('');
      setRegConfirmPassword('');
    } catch (err: any) {
      setRegError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/assets/images/mdg-logo-2017-comp-slip-1776246207408.png"
            alt="Myriad Digital Group logo"
            className="h-16 w-auto mb-4"
          />
          <h1 className="text-2xl font-heading font-bold text-foreground">Helpdesk Portal</h1>
        </div>

        {/* Login Card */}
        <div className="bg-card border border-border rounded-xl shadow-sm p-8 mb-4">
          <h2 className="text-lg font-heading font-semibold text-foreground mb-6">Sign in to your account</h2>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} suppressHydrationWarning>
            <div className="space-y-4" suppressHydrationWarning>
              <div suppressHydrationWarning>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  suppressHydrationWarning
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="you@example.com"
                />
              </div>

              <div suppressHydrationWarning>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-foreground">
                    Password
                  </label>
                  <Link
                    href="/forgot-password"
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  suppressHydrationWarning
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Create Account Toggle */}
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => {
              setShowCreateAccount(!showCreateAccount);
              setRegError('');
              setRegSuccess('');
            }}
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors"
          >
            <div>
              <p className="text-sm font-semibold text-foreground font-heading">Create Account</p>
              <p className="text-xs text-muted-foreground mt-0.5">Request access to the portal</p>
            </div>
            <svg
              className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${showCreateAccount ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCreateAccount && (
            <div className="px-6 pb-6 border-t border-border pt-5">
              {regSuccess ? (
                <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-emerald-700">{regSuccess}</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-4">
                    Enter your email and a password. Your account will be reviewed and activated by our team.
                  </p>

                  {regError && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                      {regError}
                    </div>
                  )}

                  <form onSubmit={handleCreateAccount} suppressHydrationWarning>
                    <div className="space-y-3" suppressHydrationWarning>
                      <div suppressHydrationWarning>
                        <label htmlFor="reg-email" className="block text-xs font-medium text-foreground mb-1.5">
                          Email address <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="reg-email"
                          type="email"
                          autoComplete="off"
                          required
                          suppressHydrationWarning
                          value={regEmail}
                          onChange={(e) => setRegEmail(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                          placeholder="you@example.com"
                        />
                      </div>

                      <div suppressHydrationWarning>
                        <label htmlFor="reg-password" className="block text-xs font-medium text-foreground mb-1.5">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="reg-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          suppressHydrationWarning
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                          placeholder="Min. 6 characters"
                        />
                      </div>

                      <div suppressHydrationWarning>
                        <label htmlFor="reg-confirm-password" className="block text-xs font-medium text-foreground mb-1.5">
                          Confirm password <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="reg-confirm-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          suppressHydrationWarning
                          value={regConfirmPassword}
                          onChange={(e) => setRegConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                          placeholder="Re-enter password"
                        />
                        {regConfirmPassword && regPassword !== regConfirmPassword && (
                          <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={regLoading}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {regLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Submitting…
                        </>
                      ) : (
                        'Request Account'
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
